/**
 * Auth store: persists access + refresh tokens in localStorage, exposes the cached
 * `me` profile, and emits a `change` event so the shell can re-render banners.
 *
 * Refresh tokens are kept in localStorage so the PWA works offline-first. When
 * native-app shells (RN/Flutter) wrap this PWA later, they can move refresh
 * tokens to platform-secure storage.
 */

const ACCESS_KEY = 'triviana.access_token';
const REFRESH_KEY = 'triviana.refresh_token';
const ME_KEY = 'triviana.me';

// Bump when stored auth state shape changes OR you want every visitor's
// localStorage wiped once at next boot (e.g., after a prod user-table wipe
// that invalidates every outstanding JWT). main.js checks this on startup.
const STORAGE_VERSION = '2026-06-05-1';
const VERSION_KEY = 'triviana.storage_version';

const listeners = new Set();

function emit() {
  for (const fn of listeners) {
    try { fn(); } catch (e) { console.error('auth listener error', e); }
  }
}

function readJson(key) {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try { return JSON.parse(raw); } catch { return null; }
}

export const auth = {
  getAccessToken: () => localStorage.getItem(ACCESS_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_KEY),
  getMe: () => readJson(ME_KEY),

  isAuthenticated: () => !!localStorage.getItem(ACCESS_KEY),
  isVerified: () => {
    const me = readJson(ME_KEY);
    return !!me && !!me.email_verified;
  },

  setSession(tokens) {
    if (tokens?.access_token) localStorage.setItem(ACCESS_KEY, tokens.access_token);
    if (tokens?.refresh_token) localStorage.setItem(REFRESH_KEY, tokens.refresh_token);
    emit();
  },


  setMe(me) {
    // Only emit when the payload actually changed. Pages routinely cache the
    // freshest /me into auth, which used to refire `change` → router re-render
    // → the page re-fetches /me → setMe again, an infinite loop that flooded
    // the network with calls. JSON compare is fine here: `me` is a flat user
    // object with no functions or cycles.
    const next = me ? JSON.stringify(me) : null;
    const prev = localStorage.getItem(ME_KEY);
    if (next === prev) return;
    if (next) localStorage.setItem(ME_KEY, next);
    else localStorage.removeItem(ME_KEY);
    emit();
  },

  clear() {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(ME_KEY);
    emit();
  },

  onChange(fn) {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

/**
 * Wipe local auth state if the stored version doesn't match the bundled one.
 * Called once at boot. Returns true iff anything was cleared (caller can use
 * that to e.g. show a "your session was reset" toast — currently silent).
 */
export function ensureStorageVersion() {
  const stored = localStorage.getItem(VERSION_KEY);
  if (stored === STORAGE_VERSION) return false;
  // Wipe auth keys only — leave anything else the app might persist alone.
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(ME_KEY);
  localStorage.setItem(VERSION_KEY, STORAGE_VERSION);
  return true;
}
